import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, watch } from 'vue';
import Chart from 'chart.js/auto';
import DatePicker from '@/components/DatePicker.vue';
import { DashboardApi, StringStringKeyValueModel } from '@/openapi';

enum SourceType {
    MAWB,
    MRN,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Dashboard',
  setup(__props) {

const api = new DashboardApi(undefined, '');
const maxDate = new Date().toISOString().split('T')[0];
const minDate = new Date(new Date().setDate(new Date().getDate() - 90))
    .toISOString()
    .split('T')[0];

const customDateRange = ref({ start: minDate, end: maxDate });
const selectedMAWB = ref('');
const selectedMRN = ref('');
const mawbs = ref<StringStringKeyValueModel[]>([]);
const mrns = ref<StringStringKeyValueModel[]>([]);
const loadingAverageClearanceTime = ref(true);
const loadingMawbAndMrn = ref(true);
const loadingClearedShipments = ref(false);
let averageClearanceChart: Chart | null = null;
let clearedShipmentsChart: Chart | null = null;

const averageClearanceData = {
    labels: ['Today', 'Last 7 Days', 'Last 30 Days', 'Last 180 Days', 'Custom'],
    datasets: [
        {
            label: 'Average Clearance Time (minutes)',
            data: [0, 0, 0, 0, 0],
            backgroundColor: 'rgba(75, 192, 192, 0.2)',
            borderColor: 'rgba(75, 192, 192, 1)',
            borderWidth: 1,
        },
    ],
};

const clearedShipmentsData = {
    labels: [
        'Total Shipments',
        'Cleared Shipments',
        'Pre-cleared Shipments',
        'Not Cleared Shipments',
    ],
    datasets: [
        {
            display: false,
            data: [0, 0, 0, 0],
            backgroundColor: [
                'rgba(255, 99, 132, 0.2)',
                'rgba(54, 162, 235, 0.2)',
                'rgba(255, 206, 86, 0.2)',
                'rgba(75, 192, 192, 0.2)',
            ],
            borderColor: [
                'rgba(255, 99, 132, 1)',
                'rgba(54, 162, 235, 1)',
                'rgba(255, 206, 86, 1)',
                'rgba(75, 192, 192, 1)',
            ],
            borderWidth: 1,
        },
    ],
};

const getAverageClearanceTime = () => {
    loadingAverageClearanceTime.value = true;
    api.getAverageClearanceTime(
        customDateRange.value.start,
        customDateRange.value.end
    )
        .then((response) => {
            averageClearanceData.datasets[0].data = [
                response.data.today,
                response.data.last7Days,
                response.data.last30Days,
                response.data.last180Days,
                response.data.custom,
            ];
            if (averageClearanceChart) {
                averageClearanceChart.update();
            }
        })
        .finally(() => {
            loadingAverageClearanceTime.value = false;
        });
};

const getDropdowns = () => {
    loadingMawbAndMrn.value = true;
    api.getMawbAndMrn()
        .then((response) => {
            mawbs.value = response.data.mawbs ?? [];
            selectedMAWB.value = '';
            mrns.value = response.data.mrns ?? [];
            selectedMRN.value = '';
        })
        .finally(() => {
            loadingMawbAndMrn.value = false;
            getClearedShipments();
        });
};

const getClearedShipments = async () => {
    if (loadingMawbAndMrn.value) {
        return;
    }

    if (!selectedMRN.value && !selectedMAWB.value) {
        clearedShipmentsData.datasets[0].data = [0, 0, 0, 0];
        if (clearedShipmentsChart) {
            clearedShipmentsChart.update();
        }
        return;
    }

    loadingClearedShipments.value = true;
    api.getClearedShipments(selectedMAWB.value ?? '', selectedMRN.value ?? '')
        .then((response) => {
            clearedShipmentsData.datasets[0].data = [
                response.data.totalShipments,
                response.data.cleared,
                response.data.preCleared,
                response.data.notCleared,
            ];
            if (clearedShipmentsChart) {
                clearedShipmentsChart.update();
            }
        })
        .finally(() => {
            loadingClearedShipments.value = false;
        });
};

watch(
    () => customDateRange.value.start,
    (newValue) => {
        if (newValue) {
            customDateRange.value.end = '';
        }
    }
);

const toggleShipmentSource = (sourceType: SourceType) => {
    if (sourceType === SourceType.MAWB) {
        if (selectedMRN.value) {
            selectedMRN.value = '';
        }
    } else {
        if (selectedMAWB.value) {
            selectedMAWB.value = '';
        }
    }

    getClearedShipments();
};

onMounted(() => {
    getAverageClearanceTime();
    getDropdowns();

    const averageClearanceCanvas = document.getElementById(
        'averageClearanceChart'
    );
    if (averageClearanceCanvas instanceof HTMLCanvasElement) {
        const ctx1 = averageClearanceCanvas.getContext('2d');
        if (ctx1) {
            averageClearanceChart = new Chart(ctx1, {
                type: 'bar',
                data: averageClearanceData,
                options: {
                    responsive: true,
                    plugins: {
                        legend: {
                            display: true,
                        },
                    },
                },
            });
        } else {
            console.error(
                "Failed to get the 2D rendering context for 'averageClearanceChart'."
            );
        }
    } else {
        console.error(
            "Element with ID 'averageClearanceChart' is not a canvas."
        );
    }

    const clearedShipmentsCanvas = document.getElementById(
        'clearedShipmentsChart'
    );
    if (clearedShipmentsCanvas instanceof HTMLCanvasElement) {
        const ctx2 = clearedShipmentsCanvas.getContext('2d');
        if (ctx2) {
            clearedShipmentsChart = new Chart(ctx2, {
                type: 'bar',
                data: clearedShipmentsData,
                options: {
                    responsive: true,
                    plugins: {
                        legend: {
                            display: false,
                        },
                    },
                },
            });
        } else {
            console.error(
                "Failed to get the 2D rendering context for 'clearedShipmentsChart'."
            );
        }
    } else {
        console.error(
            "Element with ID 'clearedShipmentsChart' is not a canvas."
        );
    }
});

return { __sfc: true,SourceType, api, maxDate, minDate, customDateRange, selectedMAWB, selectedMRN, mawbs, mrns, loadingAverageClearanceTime, loadingMawbAndMrn, loadingClearedShipments, averageClearanceChart, clearedShipmentsChart, averageClearanceData, clearedShipmentsData, getAverageClearanceTime, getDropdowns, getClearedShipments, toggleShipmentSource, DatePicker }
}

})